import Skeleton from '@mui/material/Skeleton';
import moment from 'moment';
import { type JSX, useMemo, useState } from 'react';

import { DashboardApi } from 'Api/Dashboards/DashboardApi';
import { LineChart, LineChartProps } from 'Components/BaseCharts/LineChart';
import { ChartSeries } from 'Components/BaseCharts/charts.common';
import { CSSColors } from 'Components/Colors';
import { FormFieldDateOptionSelect } from 'Components/FormField/FormFieldDateOptionSelect/FormFieldDateOptionSelect';
import { ChangeEventType } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldSelectDark } from 'Components/FormField/FormFieldSelectDark/FormFieldSelectDark';
import { CustomDateRangeModal } from 'Components/Modal/CustomDateRangeModal/CustomDateRangeModal';
import { Text } from 'Components/Text/Text';
import { useDateFilter } from 'Hooks/DateFilter';
import { DashboardRiskOptions, DateOptionSelectOption, DefaultPastDateRangeOptions, DueDiligenceDashboardRiskType } from 'Models/Dashboards';
import { DueDiligenceModule } from 'Models/ProductModule';

import { useRiskPortfolioHistoryQuery } from '../DueDiligenceDashboard.hooks';
import styles from '../DueDiligenceDashboard.module.css';

enum Modals {
    NONE,
    CUSTOM_DATE,
}

interface PortfolioProfileOverTimeChartProps {
    dashboardApi: DashboardApi;
    module: DueDiligenceModule;
}

enum ChartType {
    INHERENT = DueDiligenceDashboardRiskType.INHERENT,
    CONTROL_EFFECTIVENESS = DueDiligenceDashboardRiskType.CONTROL_EFFECTIVENESS,
    RESIDUAL = DueDiligenceDashboardRiskType.RESIDUAL,
    ALL = 'All',
}

const DashboardRiskPortfolioChartOptions = [{ value: ChartType.ALL, label: 'All' }, ...DashboardRiskOptions];

export const isValidDashboardRiskPortfolioChartType = (value: unknown): value is ChartType => Object.values(ChartType).includes(value as ChartType);

export const PortfolioProfileOverTimeChart = (props: PortfolioProfileOverTimeChartProps): JSX.Element => {
    const [modalState, setModalState] = useState<Modals>(Modals.NONE);
    const [riskType, setRiskType] = useState<ChartType>(ChartType.ALL);
    const dateFilterHookValues = useDateFilter(DateOptionSelectOption.LAST_THREE_MONTHS);
    const { riskPortfolioHistory, riskPortfolioHistoryError } = useRiskPortfolioHistoryQuery(props.dashboardApi, props.module, dateFilterHookValues.selection.dateRange);

    const chartProps = useMemo<LineChartProps | undefined>(() => {
        if (riskPortfolioHistory) {
            const lines: ChartSeries[] = [];
            if (riskType === ChartType.INHERENT || riskType === ChartType.ALL) {
                lines.push({
                    name: DueDiligenceDashboardRiskType.INHERENT,
                    color: CSSColors.YELLOW,
                    data: riskPortfolioHistory.map((summary) => Number(summary.statistics.average_inherent_risk.toFixed(2))),
                });
            }
            if (riskType === ChartType.CONTROL_EFFECTIVENESS || riskType === ChartType.ALL) {
                lines.push({
                    name: DueDiligenceDashboardRiskType.CONTROL_EFFECTIVENESS,
                    color: CSSColors.DARK_GRAY,
                    data: riskPortfolioHistory.map((summary) => Number(summary.statistics.average_control_effectiveness.toFixed(2))),
                });
            }
            if (riskType === ChartType.RESIDUAL || riskType === ChartType.ALL) {
                lines.push({
                    name: DueDiligenceDashboardRiskType.RESIDUAL,
                    color: CSSColors.LIGHT_BLUE,
                    data: riskPortfolioHistory.map((summary) => Number(summary.statistics.average_residual_risk.toFixed(2))),
                });
            }

            return {
                xAxisLabels: riskPortfolioHistory.map(
                    (summary) =>
                        `${moment()
                            .month(summary.month.month - 1)
                            .format('MMM')} ${moment().year(summary.month.year).format('YY')}`
                ),
                yAxisInfo: { name: riskType !== ChartType.ALL ? `Average ${riskType}` : 'Average Rating', position: 'left', min: 0, max: 5, interval: 1 },
                lines,
            };
        }
    }, [riskPortfolioHistory, riskType]);

    const handleRiskTypeChange = (value: ChangeEventType) => {
        if (value && isValidDashboardRiskPortfolioChartType(value)) {
            setRiskType(value);
        }
    };

    if (riskPortfolioHistoryError) {
        return (
            <>
                <div className={styles.cellHeader}>
                    <Text variant="Header2" color="white">
                        Portfolio Profile Over Time
                    </Text>
                </div>
                <Text color="white">{riskPortfolioHistoryError}</Text>
            </>
        );
    }

    return (
        <>
            {modalState === Modals.CUSTOM_DATE && <CustomDateRangeModal datesRestriction="pastDatesOnly" hideModal={() => setModalState(Modals.NONE)} dateFilterHookValues={dateFilterHookValues} />}
            <div className={styles.cellHeader}>
                <Text variant="Header2" color="white">
                    Portfolio Profile Over Time
                </Text>
                <div className={styles.filters}>
                    <FormFieldSelectDark formFieldId="riskType" formFieldLabel="Risk Type" options={DashboardRiskPortfolioChartOptions} selectedOption={riskType} handleChange={handleRiskTypeChange} />
                    <FormFieldDateOptionSelect formFieldId="date" options={DefaultPastDateRangeOptions} onSelectCustom={() => setModalState(Modals.CUSTOM_DATE)} dateFilterHookValues={dateFilterHookValues} />
                </div>
            </div>
            {chartProps ? <LineChart {...chartProps} /> : <Skeleton variant="rounded" width={'100%'} height={300} />}
        </>
    );
};
